import React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import {
  REVIEW_FILTERS, SENTIMENTS, isTypingTarget, moveSelection, ratingProblem, shownSentiment,
} from '../components/replylog/replyLogRules'
import { getReplyLogEntry, listReplyLog, rateReplyLogEntry } from '../api/replyLog'
import useManagerStore from '../store/managerStore'

const pct = (value) => (value == null ? '—' : `${Math.round(value * 100)}%`)
const sourceShare = (kpis) => (kpis && kpis.replies ? kpis.with_source / kpis.replies : null)

const ACCENT = '#1535c8'
const BORDER = '#e0e3f0'

// Same visual language as the Inbox: white and blue bubbles, 0.5px borders, 10px timestamps.
const bubble = (role) => ({
  maxWidth: '68%',
  borderRadius: 12,
  padding: '10px 13px',
  fontSize: 13,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  background: role === 'customer' ? 'white' : ACCENT,
  color: role === 'customer' ? '#111' : 'white',
  border: role === 'customer' ? `0.5px solid ${BORDER}` : 'none',
})

const timeText = (iso) => new Date(iso).toLocaleString()

function Kpis({ kpis, t }) {
  const items = [
    [t('replyLog.kpi.replies'), kpis?.replies ?? '—'],
    [t('replyLog.kpi.withSource'), pct(sourceShare(kpis))],
    [t('replyLog.kpi.withoutSource'), kpis?.without_source ?? '—'],
    [t('replyLog.kpi.wrong'), kpis?.wrong ?? '—'],
  ]
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
      {items.map(([label, value]) => (
        <div key={label} style={{ background: 'white', border: `0.5px solid ${BORDER}`, borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontSize: 11, color: '#6b7280' }}>{label}</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: '#111' }}>{value}</div>
        </div>
      ))}
    </div>
  )
}

function ReplyTable({ rows, selectedId, onSelect, t }) {
  const th = { textAlign: 'left', fontSize: 11, fontWeight: 500, color: '#6b7280', padding: '8px 10px', borderBottom: `0.5px solid ${BORDER}` }
  return (
    <div style={{ background: 'white', border: `0.5px solid ${BORDER}`, borderRadius: 8, overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={th}>{t('replyLog.col.time')}</th>
            <th style={th}>{t('replyLog.col.channel')}</th>
            <th style={th}>{t('replyLog.col.score')}</th>
            <th style={th}>{t('replyLog.col.source')}</th>
            <th style={th}>{t('replyLog.col.sentiment')}</th>
            <th style={th}>{t('replyLog.col.review')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const selected = row.id === selectedId
            return (
              <tr
                key={row.id}
                data-testid="reply-row"
                className={row.yellow ? 'bg-amber-100' : undefined}
                onClick={() => onSelect(row.id)}
                style={{
                  cursor: 'pointer',
                  borderTop: `0.5px solid #f0f0f0`,
                  background: selected ? '#eef2ff' : row.yellow ? '#fffbeb' : 'white',
                  boxShadow: row.yellow ? 'inset 3px 0 0 #f59e0b' : 'none',
                }}
              >
                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: '#6b7280' }}>{timeText(row.created_at)}</td>
                <td style={{ padding: '8px 10px' }}>{row.channel ?? '—'}</td>
                <td style={{ padding: '8px 10px' }}>{pct(row.top_score)}</td>
                <td style={{ padding: '8px 10px' }}>
                  {row.yellow
                    ? <span className="pill" style={{ background: '#fef3c7', color: '#92400e', fontSize: 10 }}>{t('replyLog.noSource')}</span>
                    : <span className="pill" style={{ background: '#e8f0fe', color: ACCENT, fontSize: 10 }}>{t('replyLog.withSource')}</span>}
                </td>
                <td style={{ padding: '8px 10px' }}>{shownSentiment(row) ? t(`replyLog.sentiment.${shownSentiment(row)}`) : '—'}</td>
                <td style={{ padding: '8px 10px' }}>{t(`replyLog.review.${row.review}`)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// Only sources that cleared the floor are shown. None of them means the bot had no source.
// A reply the model judged not grounded is 'Không có nguồn' even when a match cleared the floor.
function SourcesLine({ hits, yellow, t }) {
  const usable = yellow ? [] : hits.filter((hit) => hit.passed_floor)
  if (usable.length === 0) {
    return <span className="pill" style={{ background: '#fef3c7', color: '#92400e', fontSize: 11 }}>{t('replyLog.noSource')}</span>
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {usable.map((hit) => (
        <span key={`${hit.source ?? 'faq'}-${hit.rank}`} className="pill" style={{ background: '#e8f0fe', color: ACCENT, fontSize: 11 }}>
          {hit.source === 'kb' ? t('replyLog.detail.kbChunk') : (hit.question ?? t('replyLog.detail.deletedFaq'))} · {pct(hit.score)}
        </span>
      ))}
    </div>
  )
}

function ReplyDetail({ tenantId, replyId, t }) {
  const queryClient = useQueryClient()
  const detail = useQuery({
    queryKey: ['reply-log', tenantId, 'detail', replyId],
    queryFn: () => getReplyLogEntry(tenantId, replyId),
    enabled: Boolean(replyId),
  })
  const [verdict, setVerdict] = useState(null)
  const [comment, setComment] = useState('')
  const [question, setQuestion] = useState(null)
  const [override, setOverride] = useState(null)

  const save = useMutation({
    mutationFn: (body) => rateReplyLogEntry(tenantId, replyId, body),
    onSuccess: () => {
      toast.success(t('replyLog.saved'))
      queryClient.invalidateQueries({ queryKey: ['reply-log', tenantId] })
    },
    onError: (error) => toast.error(error?.response?.data?.detail?.code ?? t('replyLog.saveFailed')),
  })

  if (!replyId) return <div style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>{t('replyLog.pickRow')}</div>
  if (detail.isError) return <div role="alert" style={{ padding: 16, fontSize: 13, color: '#b91c1c' }}>{t('replyLog.detailFailed')}</div>
  if (!detail.data) return <div style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>{t('replyLog.loading')}</div>

  const reply = detail.data
  // Prefilled from the customer's message when the conversation service still has it.
  const questionText = question ?? reply.customer_question ?? ''
  const problem = ratingProblem({ verdict, comment, question: questionText })
  const shown = override ?? reply.sentiment

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 14, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', color: '#6b7280', fontSize: 11 }}>
        <span>{timeText(reply.created_at)}</span>
        {reply.channel && <span className="pill" style={{ background: '#f3f4f6', color: '#374151', fontSize: 10 }}>{reply.channel}</span>}
      </div>

      {reply.customer_question != null && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <div style={bubble('customer')}>{reply.customer_question}</div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <div style={bubble('bot')}>{reply.bot_reply ?? '—'}</div>
        <span style={{ fontSize: 10, color: '#999', marginTop: 3 }}>{t('replyLog.detail.botLabel')}</span>
      </div>

      <div>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{t('replyLog.detail.sources')}</div>
        <SourcesLine hits={reply.hits} yellow={reply.yellow} t={t} />
      </div>

      <div>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{t('replyLog.detail.sentiment')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select aria-label={t('replyLog.detail.sentiment')} style={{ border: `0.5px solid ${BORDER}`, borderRadius: 8, padding: '5px 8px', fontSize: 13 }} value={shown ?? ''} onChange={(e) => setOverride(e.target.value || null)}>
            <option value="">{t('replyLog.detail.none')}</option>
            {SENTIMENTS.map((s) => <option key={s} value={s}>{t(`replyLog.sentiment.${s}`)}</option>)}
          </select>
          {override !== null && (
            <button type="button" style={{ fontSize: 12, color: ACCENT, background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setOverride(null)}>
              {t('replyLog.detail.revert', { value: t(`replyLog.sentiment.${reply.sentiment_auto ?? 'neutral'}`) })}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" aria-pressed={verdict === 'right'} style={{ borderRadius: 8, padding: '6px 12px', border: `1px solid #16a34a`, background: verdict === 'right' ? '#16a34a' : 'white', color: verdict === 'right' ? 'white' : '#15803d', cursor: 'pointer' }} onClick={() => setVerdict('right')}>{t('replyLog.verdict.right')}</button>
        <button type="button" aria-pressed={verdict === 'wrong'} style={{ borderRadius: 8, padding: '6px 12px', border: `1px solid #dc2626`, background: verdict === 'wrong' ? '#dc2626' : 'white', color: verdict === 'wrong' ? 'white' : '#b91c1c', cursor: 'pointer' }} onClick={() => setVerdict('wrong')}>{t('replyLog.verdict.wrong')}</button>
      </div>
      {verdict === 'wrong' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: 11, color: '#6b7280' }}>{t('replyLog.detail.question')}</span>
            <input aria-label={t('replyLog.detail.question')} maxLength={150} value={questionText} onChange={(e) => setQuestion(e.target.value)} style={{ marginTop: 4, width: '100%', border: `0.5px solid ${BORDER}`, borderRadius: 8, padding: '6px 8px', fontSize: 13 }} />
          </label>
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: 11, color: '#6b7280' }}>{t('replyLog.detail.comment')}</span>
            <textarea aria-label={t('replyLog.detail.comment')} rows={3} maxLength={350} value={comment} onChange={(e) => setComment(e.target.value)} style={{ marginTop: 4, width: '100%', border: `0.5px solid ${BORDER}`, borderRadius: 8, padding: '6px 8px', fontSize: 13 }} />
          </label>
        </div>
      )}
      <button
        type="button"
        disabled={!verdict || Boolean(problem) || save.isPending}
        onClick={() => save.mutate({ verdict, comment: comment || null, question: questionText || null, sentiment_override: override })}
        style={{ alignSelf: 'flex-end', borderRadius: 8, padding: '7px 14px', background: ACCENT, color: 'white', border: 'none', fontSize: 13, cursor: 'pointer', opacity: (!verdict || problem || save.isPending) ? 0.4 : 1 }}
      >
        {t('replyLog.detail.save')}
      </button>
      {verdict === 'wrong' && problem && <div style={{ fontSize: 12, color: '#b91c1c' }}>{t(`replyLog.problem.${problem}`)}</div>}
    </div>
  )
}

export default function ReplyLogPage() {
  const { t } = useTranslation()
  const tenantId = useManagerStore((s) => s.tenantId)
  const [review, setReview] = useState('all')
  const [gate, setGate] = useState('all')
  const [jev, setJev] = useState('all')
  const [selectedId, setSelectedId] = useState(null)

  const list = useQuery({
    queryKey: ['reply-log', tenantId, 'list', review, gate, jev],
    queryFn: () => listReplyLog(tenantId, { review, gate, jev, limit: 50 }),
    enabled: Boolean(tenantId),
  })
  const rows = useMemo(() => list.data?.rows ?? [], [list.data])

  useEffect(() => {
    const onKey = (event) => {
      if (isTypingTarget(event.target)) return
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
      event.preventDefault()
      setSelectedId((current) => moveSelection(rows, current, event.key === 'ArrowDown' ? 1 : -1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [rows])

  return (
    <ResponsiveShell title={t('replyLog.title')}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
        <Kpis kpis={list.data?.kpis} t={t} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <label htmlFor="reply-log-review" style={{ color: '#6b7280' }}>{t('replyLog.filter.review')}</label>
          <select id="reply-log-review" style={{ border: `0.5px solid ${BORDER}`, borderRadius: 8, padding: '5px 8px', fontSize: 13 }} value={review} onChange={(e) => setReview(e.target.value)}>
            {REVIEW_FILTERS.map((value) => <option key={value} value={value}>{t(`replyLog.filter.${value}`)}</option>)}
          </select>
          <label htmlFor="reply-log-gate" style={{ color: '#6b7280' }}>{t('replyLog.filter.gate')}</label>
          <select id="reply-log-gate" style={{ border: `0.5px solid ${BORDER}`, borderRadius: 8, padding: '5px 8px', fontSize: 13 }} value={gate} onChange={(e) => setGate(e.target.value)}>
            <option value="all">{t('replyLog.filter.all')}</option>
            <option value="no_match">{t('replyLog.filter.gateNoMatch')}</option>
          </select>
          <label htmlFor="reply-log-jev" style={{ color: '#6b7280' }}>{t('replyLog.filter.jev')}</label>
          <select id="reply-log-jev" style={{ border: `0.5px solid ${BORDER}`, borderRadius: 8, padding: '5px 8px', fontSize: 13 }} value={jev} onChange={(e) => setJev(e.target.value)}>
            <option value="all">{t('replyLog.filter.all')}</option>
            <option value="disagree">{t('replyLog.filter.jevDisagree')}</option>
          </select>
        </div>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'minmax(0, 1fr) 400px', alignItems: 'start' }}>
          <div>
            {list.isLoading
              ? <div style={{ fontSize: 13, color: '#6b7280' }}>{t('replyLog.loading')}</div>
              : <ReplyTable rows={rows} selectedId={selectedId} onSelect={setSelectedId} t={t} />}
          </div>
          <aside aria-label={t('replyLog.detail.title')} style={{ background: 'white', border: `0.5px solid ${BORDER}`, borderRadius: 8, position: 'sticky', top: 12 }}>
            <ReplyDetail key={selectedId ?? 'none'} tenantId={tenantId} replyId={selectedId} t={t} />
          </aside>
        </div>
      </div>
    </ResponsiveShell>
  )
}
